import React, { useState, useEffect, useRef, useCallback } from "react";
import { db } from "../firebase";
import {
  collection, addDoc, getDocs, query, where, orderBy, 
  serverTimestamp, onSnapshot, limit, deleteDoc, doc
} from "firebase/firestore";

// 멤버 온라인 상태 (실제로는 최근 활동 시간으로 판단)
function getMemberStatus(lastActive) {
  if (!lastActive) return { status: "offline", color: "#94a3b8", text: "오프라인" };
  
  const now = new Date();
  const lastTime = lastActive.toDate ? lastActive.toDate() : new Date(lastActive);
  const diffMinutes = (now - lastTime) / (1000 * 60);
  
  if (diffMinutes < 5) return { status: "online", color: "#10b981", text: "온라인" };
  if (diffMinutes < 30) return { status: "away", color: "#f59e0b", text: "자리비움" };
  return { status: "offline", color: "#94a3b8", text: "오프라인" };
}

// 티어 정보
function getTierInfo(mmr) {
  if (mmr == null) mmr = 0;
  if (mmr < 1000) return { name: "루키", emoji: "🥚", color: "#8d6e63" };
  if (mmr < 1400) return { name: "엘리트", emoji: "🥈", color: "#9e9e9e" };
  if (mmr < 1800) return { name: "프로", emoji: "🥇", color: "#ffc107" };
  if (mmr < 2200) return { name: "챌린저", emoji: "🏆", color: "#ff9800" };
  return { name: "레전드", emoji: "👑", color: "#9c27b0" };
}

function GuildChat({ guildId, localUser, guildMembers }) {
  const [chatMode, setChatMode] = useState("group"); // group, dm
  const [selectedMember, setSelectedMember] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const [dmMessages, setDmMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // 자동 스크롤
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  // 스크롤 효과
  useEffect(() => {
    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, [groupMessages, dmMessages, scrollToBottom]);

  // 모든 멤버 상태 업데이트 (온라인/오프라인 구분)
  useEffect(() => {
    if (!guildMembers || guildMembers.length === 0) return;
    
    const membersWithStatus = guildMembers.map(member => ({
      ...member,
      status: getMemberStatus(member.lastActive || member.lastLoginDate || member.lastLogin)
    }));
    
    // 상태별로 정렬: 온라인 > 자리비움 > 오프라인, 그 다음 이름순
    const sortedMembers = membersWithStatus.sort((a, b) => {
      const statusOrder = { "online": 0, "away": 1, "offline": 2 };
      const aOrder = statusOrder[a.status.status] || 2;
      const bOrder = statusOrder[b.status.status] || 2;
      
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      
      // 같은 상태면 이름순으로 정렬
      return (a.nickname || "").localeCompare(b.nickname || "");
    });
    
    setAllMembers(sortedMembers);
  }, [guildMembers]);

  // 길드 전체 채팅 실시간 리스너
  useEffect(() => {
    if (!guildId || chatMode !== "group") return;
    
    let unsubscribe = null;
    
    try {
      const q = query(
        collection(db, "guildChats"),
        where("guildId", "==", guildId),
        orderBy("createdAt", "desc"),
        limit(50)
      );
      
      unsubscribe = onSnapshot(q, 
        (snapshot) => {
          try {
            const messages = snapshot.docs
              .map(doc => ({ 
                id: doc.id, 
                ...doc.data(),
                createdAt: doc.data().createdAt
              }))
              .reverse();
            setGroupMessages(messages);
          } catch (error) {
            console.error("길드 채팅 데이터 처리 오류:", error);
          }
        },
        (error) => {
          console.error("길드 채팅 로딩 오류:", error);
          if (error.code === 'unavailable') {
            console.log('Firestore 연결 재시도 중...');
          }
        }
      );
    } catch (error) {
      console.error("길드 채팅 설정 오류:", error);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [guildId, chatMode]);

  // DM 채팅 실시간 리스너
  useEffect(() => {
    if (!selectedMember || chatMode !== "dm" || !localUser) return;
    
    let unsubscribe = null;
    
    try {
      const chatId = [localUser.id, selectedMember.userId].sort().join("_");
      const q = query(
        collection(db, "directMessages"),
        where("chatId", "==", chatId),
        orderBy("createdAt", "desc"),
        limit(30)
      );
      
      unsubscribe = onSnapshot(q, 
        (snapshot) => {
          try {
            const messages = snapshot.docs
              .map(doc => ({ 
                id: doc.id, 
                ...doc.data(),
                createdAt: doc.data().createdAt
              }))
              .reverse();
            setDmMessages(messages);
          } catch (error) {
            console.error("DM 데이터 처리 오류:", error);
          }
        },
        (error) => {
          console.error("DM 로딩 오류:", error);
          if (error.code === 'unavailable') {
            console.log('Firestore 연결 재시도 중...');
          }
        }
      );
    } catch (error) {
      console.error("DM 설정 오류:", error);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [selectedMember, chatMode, localUser]);

  // 메시지 전송 (길드 전체)
  const sendGroupMessage = async () => {
    console.log('sendGroupMessage 호출됨:', {
      messageInput: messageInput,
      guildId: guildId,
      localUser: localUser,
      loading: loading
    });
    
    if (!messageInput.trim()) {
      console.log('메시지가 비어있음');
      return;
    }
    
    if (!guildId) {
      console.log('guildId가 없음');
      return;
    }
    
    if (!localUser) {
      console.log('localUser가 없음');
      return;
    }
    
    if (loading) {
      console.log('로딩 중');
      return;
    }
    
    setLoading(true);
    try {
      const messageData = {
        guildId,
        userId: localUser.id,
        userNickname: localUser.nickname || localUser.name || '알 수 없음',
        message: messageInput.trim(),
        createdAt: serverTimestamp(),
        type: "text"
      };
      
      console.log('전송할 메시지 데이터:', messageData);
      
      await addDoc(collection(db, "guildChats"), messageData);
      console.log('메시지 전송 성공');
      setMessageInput("");
      
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } catch (error) {
      console.error("메시지 전송 실패:", error);
      alert("메시지 전송에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // DM 메시지 전송
  const sendDirectMessage = async () => {
    if (!messageInput.trim() || !selectedMember || !localUser || loading) return;
    
    setLoading(true);
    try {
      const chatId = [localUser.id, selectedMember.userId].sort().join("_");
      
      const messageData = {
        chatId,
        fromUserId: localUser.id,
        fromNickname: localUser.nickname || localUser.name || '알 수 없음',
        toUserId: selectedMember.userId,
        toNickname: selectedMember.nickname || selectedMember.name || '알 수 없음',
        message: messageInput.trim(),
        createdAt: serverTimestamp(),
        type: "text"
      };
      
      await addDoc(collection(db, "directMessages"), messageData);
      setMessageInput("");
      
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } catch (error) {
      console.error("DM 전송 실패:", error);
      alert("메시지 전송에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 메시지 삭제
  const deleteMessage = async (messageId, collectionName) => {
    if (!window.confirm('정말로 이 메시지를 삭제하시겠습니까?')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, collectionName, messageId));
      console.log('메시지 삭제 성공');
    } catch (error) {
      console.error('메시지 삭제 실패:', error);
      alert('메시지 삭제에 실패했습니다.');
    }
  };

  // Enter 키 처리
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      console.log('Enter 키 눌림, chatMode:', chatMode);
      if (chatMode === "group") {
        console.log('길드 전체 채팅 Enter 전송 시도');
        sendGroupMessage();
      } else {
        console.log('DM Enter 전송 시도');
        sendDirectMessage();
      }
    }
  };

  // 메시지 시간 포맷
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleTimeString("ko-KR", { 
        hour: "2-digit", 
        minute: "2-digit" 
      });
    } catch (error) {
      return "";
    }
  };

  // 메시지 렌더링
  const renderMessage = (message, isGroup = true) => {
    if (!message || !localUser) return null;
    
    const isMyMessage = message.userId === localUser.id || message.fromUserId === localUser.id;
    const senderNickname = isGroup ? message.userNickname : message.fromNickname;
    const messageText = message.message || "";
    const timestamp = message.createdAt;
    const collectionName = isGroup ? "guildChats" : "directMessages";
    
    return (
      <div
        key={message.id}
        style={{
          display: "flex",
          flexDirection: isMyMessage ? "row-reverse" : "row",
          alignItems: "flex-start",
          gap: 8,
          marginBottom: 12,
          padding: "0 16px",
          position: "relative"
        }}
      >
        {!isMyMessage && (
          <div style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #667eea, #764ba2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
            marginTop: 4
          }}>
            {senderNickname?.charAt(0) || "?"}
          </div>
        )}
        
        <div style={{
          maxWidth: "70%",
          display: "flex",
          flexDirection: "column",
          alignItems: isMyMessage ? "flex-end" : "flex-start"
        }}>
          {!isMyMessage && (
            <div style={{
              fontSize: 12,
              color: "#64748b",
              marginBottom: 2,
              fontWeight: 600
            }}>
              {senderNickname}
            </div>
          )}
          
          <div style={{
            background: isMyMessage 
              ? "linear-gradient(135deg, #3b82f6, #2563eb)"
              : "#f1f5f9",
            color: isMyMessage ? "#fff" : "#1e293b",
            padding: "10px 14px",
            borderRadius: isMyMessage 
              ? "16px 16px 4px 16px"
              : "16px 16px 16px 4px",
            fontSize: 14,
            lineHeight: 1.4,
            wordBreak: "break-word",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            position: "relative"
          }}>
            {messageText}
          </div>
          
          <div style={{
            fontSize: 10,
            color: "#94a3b8",
            marginTop: 2
          }}>
            {formatMessageTime(timestamp)}
          </div>
        </div>
        
        {/* 삭제 버튼 (내 메시지일 때만) */}
        {isMyMessage && (
          <button
            onClick={() => deleteMessage(message.id, collectionName)}
            style={{
              position: "absolute",
              top: -8,
              right: isMyMessage ? -8 : "auto",
              left: isMyMessage ? "auto" : -8,
              background: "#ef4444",
              color: "#fff",
              border: "none",
              borderRadius: "50%",
              width: 20,
              height: 20,
              fontSize: 10,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0,
              transition: "opacity 0.2s",
              zIndex: 10
            }}
            onMouseEnter={(e) => e.target.style.opacity = 1}
            onMouseLeave={(e) => e.target.style.opacity = 0}
            title="메시지 삭제"
          >
            ×
          </button>
        )}
      </div>
    );
  };

  // 멤버 카운트 계산
  const onlineCount = allMembers.filter(m => m.status?.status === "online").length;
  const awayCount = allMembers.filter(m => m.status?.status === "away").length;
  const offlineCount = allMembers.filter(m => m.status?.status === "offline").length;

  const currentMessages = chatMode === "group" ? groupMessages : dmMessages;

  return (
    <div style={{
      height: 500,
      background: "#fff",
      borderRadius: 20,
      boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
      display: "flex",
      overflow: "hidden"
    }}>
      {/* 사이드바 - 멤버 목록 */}
      <div style={{
        width: 250,
        background: "#f8fafc",
        borderRight: "2px solid #e2e8f0",
        display: "flex",
        flexDirection: "column"
      }}>
        {/* 채팅 모드 헤더 */}
        <div style={{
          padding: "16px",
          borderBottom: "2px solid #e2e8f0",
          background: "#fff"
        }}>
          <div style={{
            display: "flex",
            gap: 6,
            background: "#f1f5f9",
            borderRadius: 10,
            padding: 4
          }}>
            <button
              onClick={() => {
                setChatMode("group");
                setSelectedMember(null);
              }}
              style={{
                flex: 1,
                padding: "8px 12px",
                borderRadius: 6,
                border: "none",
                background: chatMode === "group" ? "#3b82f6" : "transparent",
                color: chatMode === "group" ? "#fff" : "#64748b",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              🌐 전체
            </button>
            <button
              onClick={() => setChatMode("dm")}
              style={{
                flex: 1,
                padding: "8px 12px",
                borderRadius: 6,
                border: "none",
                background: chatMode === "dm" ? "#3b82f6" : "transparent",
                color: chatMode === "dm" ? "#fff" : "#64748b",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              💬 개인
            </button>
          </div>
        </div>
        
        {/* 멤버 목록 */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          padding: "8px 0"
        }}>
          {chatMode === "group" ? (
            /* 전체 채팅 모드 - 모든 멤버 표시 (온라인/오프라인 구분) */
            <div style={{ padding: "0 12px" }}>
              {/* 상태별 헤더와 카운트 */}
              <div style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#64748b",
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <span>길드원 ({allMembers.length}명)</span>
                <div style={{
                  fontSize: 10,
                  display: "flex",
                  gap: 8
                }}>
                  <span style={{ color: "#10b981" }}>●{onlineCount}</span>
                  <span style={{ color: "#f59e0b" }}>●{awayCount}</span>
                  <span style={{ color: "#94a3b8" }}>●{offlineCount}</span>
                </div>
              </div>
              
              {/* 온라인 멤버 */}
              {onlineCount > 0 && (
                <>
                  <div style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: "#10b981",
                    marginBottom: 6,
                    marginTop: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 4
                  }}>
                    <span style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#10b981"
                    }}></span>
                    온라인 ({onlineCount}명)
                  </div>
                  
                  {allMembers
                    .filter(member => member.status?.status === "online")
                    .map(member => {
                      const tierInfo = getTierInfo(member.mmr || 0);
                      
                      return (
                        <div
                          key={`online-${member.userId}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 8px",
                            borderRadius: 8,
                            marginBottom: 2,
                            background: member.userId === localUser?.id ? "#dcfce7" : "transparent",
                            border: member.userId === localUser?.id ? "1px solid #bbf7d0" : "1px solid transparent"
                          }}
                        >
                          <div style={{ position: "relative" }}>
                            <div style={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              background: "linear-gradient(135deg, #667eea, #764ba2)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              fontSize: 10,
                              fontWeight: 700
                            }}>
                              {member.nickname?.charAt(0) || "?"}
                            </div>
                            <div style={{
                              position: "absolute",
                              bottom: -1,
                              right: -1,
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: "#10b981",
                              border: "1px solid #fff"
                            }} />
                          </div>
                          
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#374151",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }}>
                              {member.nickname}
                              {member.userId === localUser?.id && " (나)"}
                            </div>
                            <div style={{
                              fontSize: 9,
                              color: "#10b981",
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              fontWeight: 600
                            }}>
                              <span style={{ color: tierInfo.color }}>
                                {tierInfo.emoji}
                              </span>
                              {member.guildTier || "멤버"}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </>
              )}
              
              {/* 자리비움 멤버 */}
              {awayCount > 0 && (
                <>
                  <div style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: "#f59e0b",
                    marginBottom: 6,
                    marginTop: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 4
                  }}>
                    <span style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#f59e0b"
                    }}></span>
                    자리비움 ({awayCount}명)
                  </div>
                  
                  {allMembers
                    .filter(member => member.status?.status === "away")
                    .map(member => {
                      const tierInfo = getTierInfo(member.mmr || 0);
                      
                      return (
                        <div
                          key={`away-${member.userId}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 8px",
                            borderRadius: 8,
                            marginBottom: 2,
                            background: member.userId === localUser?.id ? "#fef3c7" : "transparent",
                            border: member.userId === localUser?.id ? "1px solid #fde68a" : "1px solid transparent"
                          }}
                        >
                          <div style={{ position: "relative" }}>
                            <div style={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              background: "linear-gradient(135deg, #667eea, #764ba2)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              fontSize: 10,
                              fontWeight: 700,
                              opacity: 0.8
                            }}>
                              {member.nickname?.charAt(0) || "?"}
                            </div>
                            <div style={{
                              position: "absolute",
                              bottom: -1,
                              right: -1,
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: "#f59e0b",
                              border: "1px solid #fff"
                            }} />
                          </div>
                          
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#92400e",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }}>
                              {member.nickname}
                              {member.userId === localUser?.id && " (나)"}
                            </div>
                            <div style={{
                              fontSize: 9,
                              color: "#f59e0b",
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              fontWeight: 500
                            }}>
                              <span style={{ color: tierInfo.color }}>
                                {tierInfo.emoji}
                              </span>
                              {member.guildTier || "멤버"}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </>
              )}
              
              {/* 오프라인 멤버 */}
              {offlineCount > 0 && (
                <>
                  <div style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: "#94a3b8",
                    marginBottom: 6,
                    marginTop: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 4
                  }}>
                    <span style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#94a3b8"
                    }}></span>
                    오프라인 ({offlineCount}명)
                  </div>
                  
                  {allMembers
                    .filter(member => member.status?.status === "offline")
                    .map(member => {
                      const tierInfo = getTierInfo(member.mmr || 0);
                      
                      return (
                        <div
                          key={`offline-${member.userId}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 8px",
                            borderRadius: 8,
                            marginBottom: 2,
                            background: member.userId === localUser?.id ? "#f1f5f9" : "transparent",
                            border: member.userId === localUser?.id ? "1px solid #e2e8f0" : "1px solid transparent",
                            opacity: 0.7
                          }}
                        >
                          <div style={{ position: "relative" }}>
                            <div style={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              background: "linear-gradient(135deg, #9ca3af, #6b7280)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              fontSize: 10,
                              fontWeight: 700
                            }}>
                              {member.nickname?.charAt(0) || "?"}
                            </div>
                            <div style={{
                              position: "absolute",
                              bottom: -1,
                              right: -1,
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: "#94a3b8",
                              border: "1px solid #fff"
                            }} />
                          </div>
                          
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#6b7280",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }}>
                              {member.nickname}
                              {member.userId === localUser?.id && " (나)"}
                            </div>
                            <div style={{
                              fontSize: 9,
                              color: "#94a3b8",
                              display: "flex",
                              alignItems: "center",
                              gap: 2
                            }}>
                              <span style={{ color: tierInfo.color, opacity: 0.7 }}>
                                {tierInfo.emoji}
                              </span>
                              {member.guildTier || "멤버"}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </>
              )}
            </div>
          ) : (
            /* DM 모드 - 대화 가능한 멤버 목록 (오프라인 제외) */
            <div style={{ padding: "0 12px" }}>
              <div style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#64748b",
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                대화하기
              </div>
              
              {allMembers
                .filter(member => member.userId !== localUser?.id)
                .map(member => {
                  const tierInfo = getTierInfo(member.mmr || 0);
                  const isSelected = selectedMember?.userId === member.userId;
                  
                  return (
                    <div
                      key={member.userId}
                      onClick={() => setSelectedMember(member)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "8px",
                        borderRadius: 8,
                        marginBottom: 2,
                        background: isSelected ? "#3b82f6" : "transparent",
                        color: isSelected ? "#fff" : "#374151",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        opacity: member.status?.status === "offline" ? 0.6 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background = "#f1f5f9";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background = "transparent";
                        }
                      }}
                    >
                      <div style={{ position: "relative" }}>
                        <div style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: isSelected 
                            ? "rgba(255,255,255,0.2)"
                            : member.status?.status === "offline"
                              ? "linear-gradient(135deg, #9ca3af, #6b7280)"
                              : "linear-gradient(135deg, #667eea, #764ba2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: 11,
                          fontWeight: 700
                        }}>
                          {member.nickname?.charAt(0) || "?"}
                        </div>
                        <div style={{
                          position: "absolute",
                          bottom: -1,
                          right: -1,
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: member.status?.color || "#94a3b8",
                          border: "1px solid #fff"
                        }} />
                      </div>
                      
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13,
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}>
                          {member.nickname}
                        </div>
                        <div style={{
                          fontSize: 10,
                          opacity: 0.8,
                          display: "flex",
                          alignItems: "center",
                          gap: 2
                        }}>
                          <span style={{ 
                            color: isSelected ? "#fff" : tierInfo.color 
                          }}>
                            {tierInfo.emoji}
                          </span>
                          {member.status?.text || "오프라인"}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* 채팅 영역 */}
      <div style={{
        flex: 1,
        display: "flex",
        flexDirection: "column"
      }}>
        {/* 채팅 헤더 */}
        <div style={{
          padding: "12px 16px",
          borderBottom: "2px solid #e2e8f0",
          background: "#fff"
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
            <div style={{ fontSize: 18 }}>
              {chatMode === "group" ? "🌐" : "💬"}
            </div>
            <div>
              <div style={{
                fontSize: 14,
                fontWeight: 700,
                color: "#1e293b"
              }}>
                {chatMode === "group" 
                  ? "길드 전체 채팅" 
                  : selectedMember 
                    ? `${selectedMember.nickname}님과의 대화`
                    : "대화 상대를 선택하세요"
                }
              </div>
              <div style={{
                fontSize: 11,
                color: "#64748b"
              }}>
                {chatMode === "group" 
                  ? `${onlineCount}명 온라인 • ${awayCount}명 자리비움 • ${offlineCount}명 오프라인`
                  : selectedMember?.status?.text || ""
                }
              </div>
            </div>
          </div>
        </div>

        {/* 메시지 영역 */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          background: "#fafbfc",
          display: "flex",
          flexDirection: "column",
          padding: "12px 0"
        }}>
          {currentMessages.length === 0 ? (
            <div style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              color: "#94a3b8"
            }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>
                {chatMode === "group" ? "💬" : selectedMember ? "💌" : "👈"}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {chatMode === "group" 
                  ? "아직 메시지가 없습니다" 
                  : selectedMember 
                    ? `${selectedMember.nickname}님과의 첫 대화`
                    : "대화할 길드원을 선택하세요"
                }
              </div>
              <div style={{ fontSize: 12 }}>
                {chatMode === "group" 
                  ? "첫 번째 메시지를 보내보세요!" 
                  : selectedMember 
                    ? "안녕하세요! 라고 인사해보세요."
                    : "왼쪽에서 대화하고 싶은 멤버를 클릭하세요"
                }
              </div>
            </div>
          ) : (
            currentMessages.map(message => renderMessage(message, chatMode === "group"))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* 입력 영역 */}
        {(chatMode === "group" || selectedMember) && (
          <div style={{
            padding: "12px 16px",
            borderTop: "2px solid #e2e8f0",
            background: "#fff"
          }}>
            <div style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-end"
            }}>
              <textarea
                ref={inputRef}
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={chatMode === "group" 
                  ? "길드원들에게 메시지를 보내세요..." 
                  : `${selectedMember?.nickname}님에게 메시지를 보내세요...`
                }
                disabled={loading}
                style={{
                  flex: 1,
                  minHeight: 36,
                  maxHeight: 100,
                  padding: "8px 12px",
                  borderRadius: 10,
                  border: "2px solid #e2e8f0",
                  fontSize: 13,
                  lineHeight: 1.4,
                  resize: "none",
                  outline: "none",
                  fontFamily: "inherit",
                  opacity: loading ? 0.7 : 1
                }}
                onFocus={(e) => e.target.style.borderColor = "#3b82f6"}
                onBlur={(e) => e.target.style.borderColor = "#e2e8f0"}
              />
              
              <button
                onClick={() => {
                  console.log('전송 버튼 클릭됨, chatMode:', chatMode);
                  if (chatMode === "group") {
                    console.log('길드 전체 채팅 전송 시도');
                    sendGroupMessage();
                  } else {
                    console.log('DM 전송 시도');
                    sendDirectMessage();
                  }
                }}
                disabled={!messageInput.trim() || loading}
                style={{
                  padding: "8px 16px",
                  borderRadius: 10,
                  border: "none",
                  background: (messageInput.trim() && !loading)
                    ? "linear-gradient(135deg, #3b82f6, #2563eb)"
                    : "#e2e8f0",
                  color: (messageInput.trim() && !loading) ? "#fff" : "#94a3b8",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: (messageInput.trim() && !loading) ? "pointer" : "not-allowed",
                  transition: "all 0.2s",
                  minWidth: 50
                }}
              >
                {loading ? "..." : "전송"}
              </button>
            </div>
            
            <div style={{
              fontSize: 10,
              color: "#94a3b8",
              marginTop: 6,
              textAlign: "center"
            }}>
              Enter로 전송 • Shift+Enter로 줄바꿈
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GuildChat;